"use client";

import React from "react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { useDismissibleWarningsStore } from "@/store/dismissibleWarningsStore";
import { X, AlertTriangle } from "lucide-react";

interface UnauthorizedBehaviorWarningProps {
  warningId?: string;
  className?: string;
}

export const UnauthorizedBehaviorWarning: React.FC<UnauthorizedBehaviorWarningProps> = ({
  warningId = "unauthorized-behavior-warning",
  className,
}) => {
  const { isDismissed, dismissWarning } = useDismissibleWarningsStore();

  // Component only handles dismissal logic - parent controls visibility
  if (isDismissed(warningId)) {
    return null;
  }

  return (
    <div className={cn("transition-all duration-300 ease-in-out", "animate-in slide-in-from-top-2 fade-in", className)}>
      <Alert
        variant="warning"
        className={cn(
          "relative bg-[var(--color-cede-bg-component)]",
          "backdrop-blur-sm rounded-lg border border-cede-border-container shadow-sm",
          "flex items-start gap-3 p-3",
        )}
      >
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0" />
            <AlertTitle className="text-white font-semibold text-md">Important: Fair Trading Policy</AlertTitle>
          </div>
          <AlertDescription className="text-white text-sm leading-relaxed">
            Multi-accounting (e.g. the same user trading identical amounts across different wallets, or the same user
            owns different wallets) or using automated tools is not allowed. Such behavior may result in losing rewards
            or account suspension.
          </AlertDescription>
        </div>
        <button
          onClick={() => dismissWarning(warningId)}
          className={cn(
            "h-6 w-6 flex items-center justify-center flex-shrink-0 cursor-pointer",
            "text-white/70 hover:text-white",
            "hover:bg-white/10 rounded-full",
            "transition-colors duration-200",
            "outline-none focus:outline-none",
          )}
          aria-label="Dismiss warning"
        >
          <X className="h-4 w-4" />
        </button>
      </Alert>
    </div>
  );
};
